/**
 * MonsterEditor Component - Monster Editing Interface
 * 
 * Provides a comprehensive interface for editing monster templates and creating
 * custom monsters. Similar to CharacterEditor but tailored for monster-specific
 * properties like Challenge Rating, damage immunities, and special abilities.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DataService from '../services/DataService';
import UnifiedDataManager from '../services/UnifiedDataManager';
import CustomAssetManager from '../services/CustomAssetManager';
import ImageUpload from './ImageUpload/ImageUpload';
import MonsterSheetViewer from './MonsterSheetViewer/MonsterSheetViewer';
import ImageWithFallback from './ImageWithFallback';
import { resolveMonsterArtUrl } from '../services/ArtworkResolver';
import MonsterDataNormalizer from '../utils/MonsterDataNormalizer';
import HPFormatBridge from '../utils/HPFormatBridge';
import SmartPasteInterface from './SmartPasteInterface/SmartPasteInterface';
// @date_of_change: 2025-10-09 - Add ImageBrowser for selecting previously uploaded images
import ImageBrowser from './ImageBrowser/ImageBrowser';
// date_of_change: 2025-09-01 - Removed duplicate MonsterDataValidator, using unified DataService validation
import './ActionEditor.css';
import './MonsterEditor.css';
import './SaveModal.css'; // Add CSS for save modal
import { DEBUG_ENABLED } from '../utils/DebugLogger';

const debugLog = (...args) => {
  if (DEBUG_ENABLED) {
    console.debug(...args);
  }
};

const MonsterEditor = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  
  // Component state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  // @date_of_change: 2025-10-09 - Add ImageBrowser state
  const [showImageBrowser, setShowImageBrowser] = useState(false);
  // date_of_change: 2026-04-27 - Collapse artwork upload behind a toggle for compact editor layout
  const focusArtwork = new URLSearchParams(window.location.search).get('focus') === 'artwork';
  const [showArtworkUpload, setShowArtworkUpload] = useState(focusArtwork);

  useEffect(() => {
    if (!loading && focusArtwork) {
      document.getElementById('monster-artwork')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [focusArtwork, loading]);
  
  // Monster data state (aligned with DataService structure)
  const [monsterData, setMonsterData] = useState({
    // Core properties
    name: '',
    type: 'monster',
    cr: '1/4',
    hp: 10,
    maxHp: 10,
    ac: 10,
    speed: 30,
    initiative: 0,
    
    // Monster-specific properties
    size: 'Medium',
    monsterType: 'humanoid',
    alignment: 'neutral',
    
    // Ability scores (use abilities format to match normalizer)
    abilities: {
      strength: 10, dexterity: 10, constitution: 10, intelligence: 10, wisdom: 10, charisma: 10
    },
    
    // Combat and special properties
    skills: {},
    savingThrows: {},
    damageResistances: [],
    damageImmunities: [],
    conditionImmunities: [],
    senses: [],
    languages: [],
    
    // Actions and abilities
    actions: [],
    specialAbilities: [],
    legendaryActions: [],
    reactions: [],
    
    // Artwork
    artwork: '/assets/monsters/default-monster.png'
  });

  // @date_of_change 2025-08-30
  // Safe stub for workflow test runner to prevent build-time failures
  // See rationale in previous commit notes. This non-hook function keeps the button handler unchanged.
  function runWorkflowTest() {
    debugLog('[MonsterEditor] Workflow test module is not available in the production bundle. This is a no-op stub.');
    return { status: 'unavailable', reason: 'test_module_missing' };
  }

  // Load monster template on component mount
  useEffect(() => {
    const loadMonsterTemplate = async () => {
      try {
        setLoading(true);

        if (templateId === 'new') {
          setMonsterData((prev) => ({
            ...prev,
            id: 'new_monster_draft'
          }));
          setError(null);
          setStatusMessage('🆕 Blank monster sheet loaded. Fill fields manually or use Smart Paste.');
          return;
        }
        
        let template;
        
        // Check if this is a custom monster (ID starts with 'custom_monster_')
        if (templateId && templateId.startsWith('custom_monster_')) {
          debugLog(`[MonsterEditor] Loading custom monster with ID: ${templateId}`);
          // GMA-created actors live in S3 and may not exist in this browser's
          // local cache, so use the authenticated asynchronous loader.
          const customAsset = await CustomAssetManager.loadCustomAssetAsync(templateId);
          
          if (!customAsset) {
            throw new Error(`Custom monster "${templateId}" not found`);
          }
          
          // Handle potentially double-nested customData for backward compatibility
          let data = customAsset.customData || {};
          if (data.customData && typeof data.customData === 'object') {
            data = data.customData;
          }

          // Use custom monster data, ensuring top-level asset properties take precedence
          template = {
            ...data,
            id: customAsset.id, // Preserve the asset's unique ID
            name: customAsset.name,
            artwork: customAsset.artwork,
            baseTemplate: customAsset.baseTemplate, // Ensure the correct baseTemplate is used
          };

          debugLog('[MonsterEditor] Loaded and processed custom monster template:', template);
        } else {
          // Load from DataService using new comprehensive schema
          if (!templateId) {
            throw new Error(`Monster template ID not provided`);
          }
          
          // date_of_change: 2026-04-28 - Try DataService first: it calls MonsterDataLoader.toLegacyFormat
          // which returns a FLAT object with all editor-expected fields (hp, ac, abilities, speed,
          // monsterType, tokenAssetKey, artwork, actions with descriptions, etc.).
          // UDM is the fallback for custom/remote monsters not in the local bundle — it returns
          // raw canonical nested data (defense.hp, taxonomy.type) which left editor fields blank.
          try {
            try {
              template = await DataService.getMonsterData(templateId);
              if (template) {
                debugLog('[MonsterEditor] Loaded via DataService (flat legacy format):', template.name);
              }
            } catch (dsErr) {
              debugLog('[MonsterEditor] DataService load failed, trying UnifiedDataManager:', dsErr?.message);
            }

            // Fallback to UDM for custom/remote monsters not in the local bundle
            if (!template) {
              try {
                const entity = await UnifiedDataManager.loadEntity(templateId, 'monster');
                if (entity && entity.data) {
                  template = { ...entity.data };
                  debugLog('[MonsterEditor] Loaded canonical entity via UnifiedDataManager (fallback)');
                }
              } catch (udmErr) {
                debugLog('[MonsterEditor] UnifiedDataManager load also failed:', udmErr?.message);
              }
            }
            debugLog('[MonsterEditor] Loaded template:', template?.name);
            
            // 🔍 DEBUG - Dashboard JSON Load Phase
            debugLog('🔍 DEBUG - Dashboard JSON loaded:', {
              name: template.name,
              actions: template.actions,
              actionsCount: template.actions?.length || 0,
              hp: template.hp,
              ac: template.ac,
              abilities: template.abilities,
              allKeys: Object.keys(template),
              fullData: template
            });
          } catch (error) {
            throw new Error(`Monster template "${templateId}" not found: ${error.message}`);
          }
        }
        
        // date_of_change: 2025-10-17 - ZERO TRANSFORMATION after ingest (AGENTS.md)
        // Use template data as-is for editor view; do not normalize the full object.
        // date_of_change: 2026-04-27 - Ensure array fields used by .join() in the editor
        // always resolve to arrays, even when the source template omits them.
        const normalizedTemplate = {
          ...template,
          damageImmunities: Array.isArray(template.damageImmunities) ? template.damageImmunities
            : (typeof template.damageImmunities === 'string' && template.damageImmunities ? template.damageImmunities.split(',').map(s => s.trim()).filter(Boolean) : []),
          damageResistances: Array.isArray(template.damageResistances) ? template.damageResistances
            : (typeof template.damageResistances === 'string' && template.damageResistances ? template.damageResistances.split(',').map(s => s.trim()).filter(Boolean) : []),
          conditionImmunities: Array.isArray(template.conditionImmunities) ? template.conditionImmunities
            : (typeof template.conditionImmunities === 'string' && template.conditionImmunities ? template.conditionImmunities.split(',').map(s => s.trim()).filter(Boolean) : []),
          senses: Array.isArray(template.senses) ? template.senses : [],
          languages: Array.isArray(template.languages) ? template.languages : [],
        };
        debugLog('[MonsterEditor] Using template data as-is for editor view (no full normalization)');
        debugLog('🔍 DEBUG - Dashboard view data:', {
          name: normalizedTemplate.name,
          actions: normalizedTemplate.actions,
          actionsCount: normalizedTemplate.actions?.length || 0,
          hp: normalizedTemplate.hp,
          ac: normalizedTemplate.ac,
          abilities: normalizedTemplate.abilities,
          allKeys: Object.keys(normalizedTemplate)
        });
        // date_of_change: 2025-09-01 - Simplified validation using unified system - basic data checks
        debugLog('[MonsterEditor] Template loaded:', template?.name || 'unnamed');
        debugLog('[MonsterEditor] Normalized data:', normalizedTemplate?.name || 'unnamed');
        
        // Basic validation - ensure core fields exist
        if (!template?.name) {
          debugLog('[MonsterEditor] Template missing name field');
          setError('Monster template is missing name');
        }
        if (!normalizedTemplate?.abilities) {
          debugLog('[MonsterEditor] Normalized template missing abilities');
        }
        
        setMonsterData(normalizedTemplate);
        
        debugLog('Loaded monster template:', templateId, template);
        debugLog('Set monster data:', {
          name: template.name,
          cr: template.cr,
          hp: template.hp,
          ac: template.ac,
          abilities: template.abilities,
          actions: template.actions
        });
        
        setError(null);
      } catch (err) {
        debugLog('Failed to load monster template:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadMonsterTemplate();
  }, [templateId]);

  // Handle input changes with data integrity validation
  // @date_of_change 2025-08-27 Added comprehensive data validation to prevent data loss
  const handleInputChange = (field, value) => {
    setMonsterData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      
      // date_of_change: 2025-09-01 - Simplified state validation using unified system
      debugLog('[MonsterEditor] State updated:', { 
        field: Object.keys(newData)[0], 
        hasValue: !!Object.values(newData)[0] 
      });
      
      // 🔍 DEBUG - Dashboard Edit Phase
      debugLog('🔍 DEBUG - Dashboard edit field change:', {
        field,
        value,
        totalKeys: Object.keys(newData).length,
        actionsCount: newData.actions?.length || 0,
        hasActions: !!newData.actions,
        hasHP: !!newData.hp
      });
      
      return newData;
    });
  };

  // Handle ability changes (updated to use abilities format)
  const handleAbilityChange = (ability, value) => {
    const numValue = parseInt(value) || 0;
    setMonsterData(prev => ({
      ...prev,
      abilities: {
        ...prev.abilities,
        [ability]: numValue
      }
    }));
  };

  // Removed unused handleSkillChange (date_of_change: 2025-11-09 - lint cleanup)

  // Handle array field changes (immunities, resistances, etc.)
  const handleArrayFieldChange = (field, value) => {
    const array = value.split(',').map(item => item.trim()).filter(item => item);
    setMonsterData(prev => ({
      ...prev,
      [field]: array
    }));
  };

  // Handle action changes with validation
  // @date_of_change 2025-08-27 Added action-level data validation
  const handleActionChange = (index, field, value) => {
    debugLog(`[MonsterEditor] handleActionChange: action[${index}].${field} = `, value);
    setMonsterData(prev => {
      const actions = [...prev.actions];
      const originalAction = actions[index];
      actions[index] = {
        ...actions[index],
        [field]: value
      };
      
      // Validate action data integrity
      const actionBefore = JSON.stringify(originalAction);
      const actionAfter = JSON.stringify(actions[index]);
      
      debugLog('🔍 DEBUG - Action edit validation:', {
        actionIndex: index,
        field,
        value,
        actionName: actions[index].name,
        hasDescription: !!actions[index].description,
        hasDamage: !!(actions[index].damage || actions[index].damageEntries),
        dataChanged: actionBefore !== actionAfter
      });
      
      // Special case: if changing damage or damageType legacy fields, keep damageEntries in sync
      if ((field === 'damage' || field === 'damageType') && Array.isArray(actions[index].damageEntries) && actions[index].damageEntries.length > 0) {
        // Parse the updated damage to get dice and bonus
        const { dice, bonus, average } = MonsterDataNormalizer.parseDamageFormula(actions[index].damage || '1d6');
        
        // Update the first damage entry
        actions[index].damageEntries[0] = {
          ...actions[index].damageEntries[0],
          dice: dice,
          bonus: bonus,
          formula: actions[index].damage || '1d6',
          average: average,
          type: field === 'damageType' ? value : actions[index].damageEntries[0].type
        };
        
        // Generate updated description
        const description = MonsterDataNormalizer.buildAttackDescription(actions[index]);
        actions[index].description = description;
      }
      
      return {
        ...prev,
        actions
      };
    });
  };
  
  // Handle damage entry change
  const handleDamageEntryChange = (actionIndex, entryIndex, field, value) => {
    debugLog(`[MonsterEditor] handleDamageEntryChange: action[${actionIndex}].damageEntries[${entryIndex}].${field} = `, value);
    setMonsterData(prev => {
      const actions = [...prev.actions];
      const action = {...actions[actionIndex]};
      
      // Ensure damageEntries array exists
      if (!Array.isArray(action.damageEntries)) {
        action.damageEntries = [
          {
            type: action.damageType || 'bludgeoning',
            dice: '1d6',
            bonus: 0,
            formula: action.damage || '1d6',
            average: 3.5
          }
        ];
      }
      
      // Update the specific field in the damage entry
      action.damageEntries[entryIndex] = {
        ...action.damageEntries[entryIndex],
        [field]: value
      };
      
      // Special handling for formula field to update dice, bonus, and average
      if (field === 'formula') {
        const { dice, bonus, average } = MonsterDataNormalizer.parseDamageFormula(value);
        action.damageEntries[entryIndex].dice = dice;
        action.damageEntries[entryIndex].bonus = bonus;
        action.damageEntries[entryIndex].average = average;
      }
      
      // Keep legacy fields in sync with the first damage entry
      if (entryIndex === 0) {
        if (field === 'formula') {
          action.damage = value;
        } else if (field === 'type') {
          action.damageType = value;
        }
      }
      
      // Generate updated description
      action.description = MonsterDataNormalizer.buildAttackDescription(action);
      
      actions[actionIndex] = action;
      
      return {
        ...prev,
        actions
      };
    });
  };
  
  // Add a damage entry to an action
  const addDamageEntry = (actionIndex) => {
    console.debug(`[MonsterEditor] Adding damage entry to action ${actionIndex}`);
    
    setMonsterData(prev => {
      const actions = [...prev.actions];
      const action = {...actions[actionIndex]};
      
      // Make sure damageEntries exists and is an array
      const damageEntries = Array.isArray(action.damageEntries) ? 
        [...action.damageEntries] : [
          {
            type: action.damageType || 'bludgeoning',
            dice: '1d6',
            bonus: 0,
            formula: action.damage || '1d6',
            average: 3.5
          }
        ];
      
      // Add a new damage entry with a different damage type
      damageEntries.push({
        type: 'fire', // Default to fire as a common secondary damage type
        dice: '1d6',
        bonus: 0,
        formula: '1d6',
        average: 3.5
      });
      
      // Update the action with new damage entries
      const updatedAction = {
        ...action,
        damageEntries
      };
      
      // Generate updated description
      updatedAction.description = MonsterDataNormalizer.buildAttackDescription(updatedAction);
      
      actions[actionIndex] = updatedAction;
      
      return {
        ...prev,
        actions
      };
    });
  };
  
  // Remove a damage entry from an action
  const removeDamageEntry = (actionIndex, entryIndex) => {
    console.debug(`[MonsterEditor] Removing damage entry ${entryIndex} from action ${actionIndex}`);
    
    setMonsterData(prev => {
      const actions = [...prev.actions];
      const action = {...actions[actionIndex]};
      
      // Only proceed if damageEntries is an array with more than one entry
      if (!Array.isArray(action.damageEntries) || action.damageEntries.length <= 1) {
        return prev; // Don't remove the last damage entry
      }
      
      // Filter out the damage entry to remove
      const damageEntries = action.damageEntries.filter((_, idx) => idx !== entryIndex);
      
      // Update the action with filtered damage entries
      const updatedAction = {
        ...action,
        damageEntries,
        // Keep legacy fields in sync with first damage entry
        damage: damageEntries[0]?.formula || '',
        damageType: damageEntries[0]?.type || 'bludgeoning'
      };
      
      // Generate updated description
      updatedAction.description = MonsterDataNormalizer.buildAttackDescription(updatedAction);
      
      actions[actionIndex] = updatedAction;
      
      return {
        ...prev,
        actions
      };
    });
  };
  
  // Test attack data parsing with multiple damage types
  const testAttackParsing = () => {
    debugLog('=== TESTING ATTACK DATA PARSING ===');
    
    // Create test cases with different attack formats
    const testCases = [
      {
        name: "Simple melee attack",
        description: "Melee Weapon Attack: +5 to hit, reach 5 ft., one target. Hit: 7 (1d8+3) slashing damage."
      },
      {
        name: "Complex attack with multiple damage types",
        description: "Melee Weapon Attack: +6 to hit, reach 5 ft., one target. Hit: 8 (1d10+3) slashing damage plus 4 (1d8) fire damage."
      },
      {
        name: "Ranged attack with multiple damage types",
        description: "Ranged Weapon Attack: +7 to hit, range 80/320 ft., one target. Hit: 6 (1d6+3) piercing damage plus 3 (1d6) poison damage and 2 (1d4) acid damage."
      },
      {
        name: "Spell attack with saving throw",
        description: "The creature exhales fire in a 15-foot cone. Each creature in that area must make a DC 15 Dexterity saving throw, taking 24 (7d6) fire damage on a failed save, or half as much damage on a successful one."
      }
    ];
    
    // Test each case
    testCases.forEach(testCase => {
      debugLog(`\nTesting: ${testCase.name}`);
      debugLog(`Description: ${testCase.description}`);
      
      // Extract damage entries using normalizer
      const damageEntries = MonsterDataNormalizer.extractDamageEntries(testCase);
      debugLog('Extracted damage entries:', damageEntries);
      
      // Extract attack properties
      const attackBonus = MonsterDataNormalizer.extractAttackBonus(testCase.description);
      debugLog('Attack bonus:', attackBonus);
      
      const reachRange = MonsterDataNormalizer.extractReachRange(testCase.description);
      debugLog('Reach/Range:', reachRange);
      
      const savingThrow = MonsterDataNormalizer.extractSavingThrow(testCase.description);
      debugLog('Saving throw:', savingThrow);
      
      // Test full action normalization if available
      if (typeof MonsterDataNormalizer.normalizeAction === 'function') {
        const normalizedAction = MonsterDataNormalizer.normalizeAction(testCase);
        debugLog('Normalized action:', normalizedAction);
      }
      
      debugLog('---------------------------------------------------');
    });
    
    // Also test a current action if one exists
    if (monsterData.actions && monsterData.actions.length > 0) {
      const currentAction = monsterData.actions[0];
      debugLog('\nTesting current action:', currentAction.name);
      debugLog('Current action data:', currentAction);
      
      // Generate description from current action
      const generatedDescription = MonsterDataNormalizer.buildAttackDescription(currentAction);
      debugLog('Generated description:', generatedDescription);
      
      // Extract damage entries from current action
      const extractedDamageEntries = MonsterDataNormalizer.extractDamageEntries(currentAction);
      debugLog('Re-extracted damage entries:', extractedDamageEntries);
    }
    
    debugLog('\n=== TEST COMPLETE ===');
    alert('Attack data parsing test complete. Check browser console for results.');
  };

  // Add new action
  const addAction = () => {
    setMonsterData(prev => ({
      ...prev,
      actions: [
        ...prev.actions,
        {
          name: 'New Action',
          attackType: 'melee',
          attackBonus: 0,
          reach: 5,
          range: null,
          targets: 'one target',
          damageEntries: [
            {
              type: 'bludgeoning',
              dice: '1d6',
              bonus: 0,
              formula: '1d6',
              average: 3.5
            }
          ],
          // Legacy fields for backward compatibility
          type: 'melee',
          damage: '1d6',
          damageType: 'bludgeoning',
          description: 'Melee weapon attack: +0 to hit, reach 5 ft., one target. Hit: 3 (1d6) bludgeoning damage.'
        }
      ]
    }));
  };

  // Remove action
  const removeAction = (index) => {
    setMonsterData(prev => ({
      ...prev,
      actions: prev.actions.filter((_, i) => i !== index)
    }));
  };

  // Handle image upload
  // @date_of_change: 2025-10-09 - Save to BOTH 'tokenAssetKey' (canonical) and 'artwork' (display fallback)
  const handleImageUploaded = (imagePath) => {
    debugLog('[MonsterEditor] Image uploaded:', imagePath);
    debugLog('[MonsterEditor] Setting monster artwork to:', imagePath);
    setMonsterData(prev => {
      const updated = {
        ...prev,
        tokenAssetKey: imagePath,  // Canonical field for resolveMonsterArtUrl
        artwork: imagePath         // Fallback for legacy code
      };
      debugLog('[MonsterEditor] Updated monster data:', updated);
      return updated;
    });
  };

  // @date_of_change: 2025-10-09 - Handle selecting image from browser
  const handleImageSelected = (imageKey) => {
    setMonsterData(prev => ({
      ...prev,
      tokenAssetKey: imageKey,
      artwork: imageKey
    }));
    setShowImageBrowser(false);
  };

  // State for save modal
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // State for smart paste interface
  const [showSmartPaste, setShowSmartPaste] = useState(false);

  // Handle initial save attempt
  const handleSaveAttempt = async () => {
    if (!monsterData.name.trim()) {
      setStatusMessage('❌ Please enter a name for your custom monster');
      return;
    }

    // Check if monster with this name already exists
    const existingAssets = await CustomAssetManager.getAllCustomAssets();
    const existingMonster = existingAssets.monsters.find(m => m.name === monsterData.name.trim());
    
    if (existingMonster && existingMonster.id === monsterData.id) {
      // Editing the loaded record is an intentional overwrite. Do not make the
      // user choose between duplicate-name options just to attach artwork.
      handleSaveMonster(true);
    } else if (existingMonster) {
      // Show save options modal instead of confirm dialog
      setShowSaveModal(true);
    } else {
      // No conflict, proceed with save
      handleSaveMonster(false);
    }
  };
  // Handle saving as new with different name
  const handleSaveAsNew = () => {
    setShowSaveModal(false);
    setMonsterData(prev => ({
      ...prev,
      name: prev.name.trim() + ' (Copy)'
    }));
    // Focus on the name input after state update
    setTimeout(() => {
      const nameInput = document.querySelector('input[name="name"]');
      if (nameInput) {
        nameInput.focus();
        nameInput.select();
      }
    }, 0);
  };

  // Smart paste handlers for MonsterEditor
  // @date_of_change 2025-09-18 - Added smart paste integration for monster content parsing
  const handleSmartPasteContent = (parsedContent, suggestions) => {
    debugLog('[MonsterEditor] Smart paste content parsed:', parsedContent);
    // Content is parsed and ready - suggestions are shown in the interface
  };

  const handleSmartPasteApply = (changeRecord) => {
    debugLog('[MonsterEditor] Applying smart paste changes:', changeRecord);

    const { field, action, data } = changeRecord;

    try {
      switch (field) {
        case 'actions':
          if (action === 'append') {
            if (typeof data === 'object' && data.name) {
              // Full action object - add to actions array
              const newAction = {
                name: data.name,
                ...data.attack // Use the parsed attack structure
              };

              setMonsterData(prev => ({
                ...prev,
                actions: [...prev.actions, newAction]
              }));

              setStatusMessage(`✅ Added action: ${data.name}`);
            }
          }
          break;

        case 'abilities':
          if (action === 'merge') {
            // Update ability scores
            setMonsterData(prev => ({
              ...prev,
              abilities: { ...prev.abilities, ...data }
            }));
            setStatusMessage(`✅ Updated ability scores`);
          }
          break;

        case 'traits':
          if (action === 'append') {
            if (typeof data === 'object' && data.name) {
              // Full trait/feature object
              const newTrait = {
                name: data.name,
                text: data.description || data.text || ''
              };

              setMonsterData(prev => ({
                ...prev,
                traits: [...(prev.traits || []), newTrait]
              }));

              setStatusMessage(`✅ Added trait: ${data.name}`);
            }
          }
          break;

        case 'all':
          if (action === 'replace') {
            // Replace entire monster (careful!)
            const confirmReplace = window.confirm(
              `This will replace all monster data with the parsed content. Are you sure?`
            );
            if (confirmReplace) {
              // Preserve ID and other important fields, but merge in new data
              const mergedData = {
                ...monsterData,
                ...data,
                id: monsterData.id
              };

              setMonsterData(mergedData);
              setStatusMessage(`✅ Imported complete monster data`);
            }
          }
          break;

        default:
          debugLog('[MonsterEditor] Unknown field for smart paste:', field);
          setStatusMessage(`⚠️ Unknown field: ${field}`);
      }

      // Clear success message after delay
      setTimeout(() => {
        setStatusMessage('');
      }, 3000);

    } catch (error) {
      debugLog('[MonsterEditor] Smart paste apply error:', error);
      setStatusMessage(`❌ Error applying changes: ${error.message}`);
    }
  };

  const handleSaveMonster = async (allowOverwrite = false) => {
    if (!monsterData) return;
    debugLog('[MonsterEditor] Preparing to save monster:', monsterData);
    setSaving(true);
    setShowSaveModal(false);
    
    // Create copy of monster data for saving
    const reviewedAt = new Date().toISOString();
    const isGmcProjection = ['gmc', 'gamemastercraft'].includes(
      String(monsterData.externalSource?.system || '').trim().toLowerCase()
    );
    const monsterToSave = {
      ...monsterData,
      name: monsterData.name.trim(),
      id: typeof monsterData.id === 'string' && monsterData.id.startsWith('custom_monster_') ? monsterData.id : `custom_monster_${monsterData.name.trim().toLowerCase().replace(/\s+/g, '-')}`,
      isCustom: true,
      lastModified: reviewedAt,
      // GMC owns identity, while an explicit editor save establishes that the
      // current VCS mechanics were reviewed rather than projection defaults.
      // date_of_change: 2026-08-01
      ...(isGmcProjection ? {
        _meta: { ...(monsterData._meta || {}), vcsMechanicalReviewAt: reviewedAt },
      } : {}),
    };
    
    debugLog('[MonsterEditor] Saving monster data:', monsterToSave);
    
    // 🔍 DEBUG - Dashboard save data validation
    debugLog('🔍 DEBUG - Dashboard saving complete monster data:', {
      name: monsterToSave.name,
      actions: monsterToSave.actions,
      actionsCount: monsterToSave.actions?.length || 0,
      allKeys: Object.keys(monsterToSave),
      dataIntegrity: {
        hasName: !!monsterToSave.name,
        hasActions: !!monsterToSave.actions && monsterToSave.actions.length > 0,
        hasHP: !!monsterToSave.hp,
        hasAC: !!monsterToSave.ac,
        hasAbilities: !!monsterToSave.abilities
      }
    });
    try {
      await CustomAssetManager.saveMonster(monsterToSave, allowOverwrite);
      // date_of_change: 2025-12-03
      // rule_reference: AGENTS.md SSOT — Monster editors must publish canonical entities into UnifiedDataManager.
      // purpose: Mirror editor-created monsters into the monster_template.json canonical library via ImportExportService,
      //          so Dashboard and the combat engine consume a single, adapter-backed monster pipeline.
      try {
        const { default: ImportExportService } = await import('../services/ImportExportService');
        await ImportExportService.importMonster({
          ...monsterToSave,
          type: 'monster'
        });
      } catch (canonicalError) {
        debugLog('[MonsterEditor] Canonical import via ImportExportService.importMonster failed:', canonicalError);
      }
      debugLog('[MonsterEditor] Monster saved successfully!');
      setStatusMessage('✅ Monster saved successfully!');
      setSaveSuccess(true);
      
      // Navigate back to dashboard after short delay
      setTimeout(() => {
        navigate('/');
      }, 1500);
      
    } catch (error) {
      debugLog('Failed to save monster:', error);
      setStatusMessage(`❌ Error saving monster: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Calculate ability modifier
  const getModifier = (score) => {
    return Math.floor((score - 10) / 2);
  };

  // Format modifier for display
  const formatModifier = (score) => {
    const mod = getModifier(score);
    return mod >= 0 ? `+${mod}` : `${mod}`;
  };

  if (loading) {
    return (
      <div className="monster-editor loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading monster template...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="monster-editor error">
        <div className="error-message">
          <h2>Error Loading Monster</h2>
          <p>{error}</p>
          <Link to="/" className="btn btn-primary">
            ← Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="monster-editor">
      {/* Header */}
      <div className="editor-header">
        <div className="header-left">
          <button 
            onClick={() => navigate('/')} 
            className="back-button"
            type="button"
          >
            ← Back to Dashboard
          </button>
          <div className="header-title">
            <h1>Monster Editor</h1>
            <span className="editing-label">Editing: {monsterData.name}</span>
          </div>
        </div>
        <div className="header-actions">
          <button
            onClick={() => setShowSmartPaste(!showSmartPaste)}
            className={`btn ${showSmartPaste ? 'btn-primary' : 'btn-secondary'}`}
            title="Paste D&D content (actions, traits, stat blocks) and have it automatically parsed and added"
          >
            {showSmartPaste ? 'Hide Smart Paste' : 'Smart Paste'}
          </button>
          <button
            onClick={() => setIsPreviewMode(!isPreviewMode)}
            className={`btn ${isPreviewMode ? 'btn-secondary' : 'btn-outline'}`}
          >
            {isPreviewMode ? 'Edit Mode' : 'Preview Mode'}
          </button>
          <button
            className="btn btn-success"
            onClick={handleSaveAttempt}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Monster'}
          </button>
        </div>
      </div>

      {/* Status message */}
      {statusMessage && (
        <div className={`status-message ${saveSuccess ? 'success' : 'error'}`}>
          {statusMessage}
        </div>
      )}
      
      {/* Save Options Modal */}
      {showSaveModal && (
        <div className="modal-overlay">
          <div className="save-options-modal">
            <h3>Monster Already Exists</h3>
            <p>A monster named <strong>"{monsterData.name.trim()}"</strong> already exists.</p>
            <p>What would you like to do?</p>
            
            <div className="modal-actions">
              <button 
                onClick={() => handleSaveMonster(true)} 
                className="btn btn-warning"
              >
                Overwrite Existing
              </button>
              <button 
                onClick={handleSaveAsNew} 
                className="btn btn-primary"
              >
                Save as New Copy
              </button>
              <button 
                onClick={() => setShowSaveModal(false)} 
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Editor Content */}
      <div className="editor-content">
        {isPreviewMode ? (
          /* Preview Mode - Show monster sheet using MonsterSheetViewer with correct template */
          <div className="monster-editor-preview">
            <MonsterSheetViewer 
              token={{
                name: monsterData.name,
                id: monsterData.id || 'preview',
                templateId: null, // Force use of token data only in preview mode
                
                // Core stats with unified HP format for consistency
                hp: monsterData.hp,
                maxHp: monsterData.maxHp,
                hpObj: HPFormatBridge.createUnifiedHP(monsterData),
                ac: monsterData.ac,
                speed: monsterData.speed,
                size: monsterData.size,
                type: monsterData.type,
                cr: monsterData.cr,
                
                // Ability scores - ensure both formats
                stats: monsterData.stats,
                abilities: monsterData.abilities,
                
                // Combat properties
                skills: monsterData.skills,
                savingThrows: monsterData.savingThrows,
                damageResistances: monsterData.damageResistances,
                damageImmunities: monsterData.damageImmunities,
                conditionImmunities: monsterData.conditionImmunities,
                senses: monsterData.senses,
                languages: monsterData.languages,
                
                // Special abilities
                traits: monsterData.traits || [],
                actions: monsterData.actions || [],
                reactions: monsterData.reactions || [],
                legendaryActions: monsterData.legendaryActions || [],
                
                // Any custom monster data (might be useful for future features)
                customData: monsterData
              }}
            />
          </div>
        ) : (
          // Edit Mode - Show form fields
          <div className="editor-form">
            {/* Smart Paste Interface */}
            {showSmartPaste && (
              <div className="form-section">
                <h3>✨ Smart Paste - Add D&D Content</h3>
                <p className="form-description">
                  Paste monster actions, traits, or complete stat blocks below and they will be automatically
                  parsed and added to the appropriate sections of your monster.
                </p>
                <SmartPasteInterface
                  onContentParsed={handleSmartPasteContent}
                  onApplyChanges={handleSmartPasteApply}
                  context={{
                    entityType: 'monster',
                    currentData: monsterData
                  }}
                  placeholder="Paste D&D monster content here - actions like 'Bite. Melee Weapon Attack: +5 to hit...', traits like 'Pack Tactics. The wolf has advantage...', or complete monster stat blocks!"
                />
              </div>
            )}

            <div className="form-section">
              <h3>Basic Information</h3>
              
              <div className="form-group">
                <label>Custom Name *</label>
                <input
                  type="text"
                  value={monsterData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Enter custom monster name"
                />
              </div>

              {/* date_of_change: 2026-04-27 - Compact artwork section: inline thumbnail + toggle for upload controls */}
              <div id="monster-artwork" className="form-group artwork-section artwork-section--compact">
                <label>Monster Artwork</label>
                <div className="artwork-inline-row">
                  <div className="artwork-thumb">
                    {monsterData.artwork ? (
                      <ImageWithFallback
                        src={resolveMonsterArtUrl(monsterData)}
                        alt="Monster token"
                        className="artwork-thumb__img"
                      />
                    ) : (
                      <div className="artwork-thumb__placeholder">No image</div>
                    )}
                  </div>
                  <div className="artwork-inline-controls">
                    <span className="artwork-filename">{monsterData.artwork || 'None'}</span>
                    <div className="artwork-btn-row">
                      <button
                        type="button"
                        onClick={() => setShowArtworkUpload(!showArtworkUpload)}
                        className="btn btn-sm btn-secondary"
                      >
                        {showArtworkUpload ? 'Hide Upload' : 'Change Image'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowImageBrowser(!showImageBrowser)}
                        className="btn btn-sm btn-secondary"
                      >
                        {showImageBrowser ? 'Hide Browser' : 'Browse Uploaded'}
                      </button>
                    </div>
                  </div>
                </div>

                {showArtworkUpload && (
                  <div className="artwork-upload-panel">
                    <ImageUpload
                      entityType="monster"
                      entityName={monsterData.name || 'custom-monster'}
                      onImageUploaded={handleImageUploaded}
                      currentImage={monsterData.artwork}
                    />
                  </div>
                )}

                {showImageBrowser && (
                  <div className="artwork-upload-panel">
                    <ImageBrowser
                      onSelectImage={handleImageSelected}
                      onClose={() => setShowImageBrowser(false)}
                      filterType="token"
                    />
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Challenge Rating</label>
                  <select
                    value={monsterData.cr}
                    onChange={(e) => handleInputChange('cr', e.target.value)}
                  >
                    <option value="0">0</option>
                    <option value="1/8">1/8</option>
                    <option value="1/4">1/4</option>
                    <option value="1/2">1/2</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Size</label>
                  <select
                    value={monsterData.size}
                    onChange={(e) => handleInputChange('size', e.target.value)}
                  >
                    <option value="Tiny">Tiny</option>
                    <option value="Small">Small</option>
                    <option value="Medium">Medium</option>
                    <option value="Large">Large</option>
                    <option value="Huge">Huge</option>
                    <option value="Gargantuan">Gargantuan</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Type</label>
                  <select
                    value={monsterData.monsterType}
                    onChange={(e) => handleInputChange('monsterType', e.target.value)}
                  >
                    <option value="beast">Beast</option>
                    <option value="humanoid">Humanoid</option>
                    <option value="undead">Undead</option>
                    <option value="fiend">Fiend</option>
                    <option value="dragon">Dragon</option>
                    <option value="fey">Fey</option>
                    <option value="elemental">Elemental</option>
                    <option value="aberration">Aberration</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Hit Points</label>
                  <input
                    type="number"
                    value={monsterData.hp}
                    onChange={(e) => handleInputChange('hp', parseInt(e.target.value) || 0)}
                    min="1"
                  />
                </div>

                <div className="form-group">
                  <label>Max Hit Points</label>
                  <input
                    type="number"
                    value={monsterData.maxHp}
                    onChange={(e) => handleInputChange('maxHp', parseInt(e.target.value) || 0)}
                    min="1"
                  />
                </div>

                <div className="form-group">
                  <label>Armor Class</label>
                  <input
                    type="number"
                    value={monsterData.ac}
                    onChange={(e) => handleInputChange('ac', parseInt(e.target.value) || 0)}
                    min="1"
                  />
                </div>

                <div className="form-group">
                  <label>Speed (ft.)</label>
                  <input
                    type="number"
                    value={monsterData.speed}
                    onChange={(e) => handleInputChange('speed', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </div>
              </div>
            </div>


            <div className="form-section">
              <h3>Ability Scores</h3>
              <div className="abilities-grid">
                {Object.entries(monsterData.abilities).map(([ability, value]) => (
                  <div key={ability} className="ability-input">
                    <label>{ability.toUpperCase()}</label>
                    <input
                      type="number"
                      value={value}
                      onChange={(e) => handleAbilityChange(ability, e.target.value)}
                      min="1"
                      max="30"
                    />
                    <span className="modifier">({formatModifier(value)})</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="actions-container">
      <h3>Actions</h3>
      <div className="actions-header-controls">
        <button onClick={testAttackParsing} className="test-btn">Test Attack Parsing</button>
        <button onClick={() => {
          const results = runWorkflowTest();
          debugLog('Workflow test complete:', results);
          alert('Monster attack workflow test complete. Check browser console for results.');
        }} className="test-btn">Test Workflow</button>
      </div>
      {monsterData.actions.map((action, index) => (
        <div key={index} className="action-editor">
          <div className="action-header">
            <h4>Action {index + 1}</h4>
            <button 
              onClick={() => removeAction(index)}
              className="btn btn-danger btn-sm"
            >
              Remove
            </button>
          </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Name</label>
                      <input
                        type="text"
                        value={action.name}
                        onChange={(e) => handleActionChange(index, 'name', e.target.value)}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Attack Type</label>
                      <select
                        value={action.attackType || 'melee'}
                        onChange={(e) => handleActionChange(index, 'attackType', e.target.value)}
                      >
                        <option value="melee">Melee Weapon</option>
                        <option value="meleeSpell">Melee Spell</option>
                        <option value="ranged">Ranged Weapon</option>
                        <option value="rangedSpell">Ranged Spell</option>
                        <option value="area">Area Effect</option>
                        <option value="save">Saving Throw</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Attack Bonus</label>
                      <input
                        type="number"
                        value={action.attackBonus || 0}
                        onChange={(e) => handleActionChange(index, 'attackBonus', parseInt(e.target.value) || 0)}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Reach (ft.)</label>
                      <input
                        type="number"
                        value={action.reach || 5}
                        onChange={(e) => handleActionChange(index, 'reach', parseInt(e.target.value) || 5)}
                        disabled={action.attackType?.includes('ranged')}
                      />
                    </div>
                  </div>
                  
                  {/* Range section for ranged attacks */}
                  {action.attackType?.includes('ranged') && (
                    <div className="form-row">
                      <div className="form-group">
                        <label>Range Normal (ft.)</label>
                        <input
                          type="number"
                          value={(action.range?.normal) || 30}
                          onChange={(e) => handleActionChange(index, 'range', {
                            ...action.range,
                            normal: parseInt(e.target.value) || 30
                          })}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Range Long (ft.)</label>
                        <input
                          type="number"
                          value={(action.range?.long) || 60}
                          onChange={(e) => handleActionChange(index, 'range', {
                            ...action.range,
                            long: parseInt(e.target.value) || 60
                          })}
                        />
                      </div>
                    </div>
                  )}
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Targets</label>
                      <input
                        type="text"
                        value={action.targets || 'one target'}
                        onChange={(e) => handleActionChange(index, 'targets', e.target.value)}
                        placeholder="e.g., one target, all creatures in a 15-foot cone"
                      />
                    </div>
                  </div>

                  {/* Area Section */}
                  <div className="area-section">
                    <h5>Area (optional)</h5>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Area Type</label>
                        <select
                          value={action.areaType || ''}
                          onChange={(e) => handleActionChange(index, 'areaType', e.target.value || null)}
                        >
                          <option value="">None</option>
                          <option value="cone">Cone</option>
                          <option value="line">Line</option>
                          <option value="cube">Cube</option>
                          <option value="sphere">Sphere</option>
                          <option value="cylinder">Cylinder</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Area Size</label>
                        <input
                          type="number"
                          value={action.areaSize ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            handleActionChange(index, 'areaSize', v === '' ? null : (parseInt(v) || 0));
                          }}
                          placeholder="e.g., 15"
                          min="0"
                        />
                      </div>

                      <div className="form-group">
                        <label>Area Unit</label>
                        <select
                          value={action.areaUnit || 'ft'}
                          onChange={(e) => handleActionChange(index, 'areaUnit', e.target.value)}
                        >
                          <option value="ft">ft</option>
                          <option value="mile">mile</option>
                          <option value="m">m</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Area Targets</label>
                        <input
                          type="text"
                          value={action.areaTargets || ''}
                          onChange={(e) => handleActionChange(index, 'areaTargets', e.target.value)}
                          placeholder="e.g., each creature in that area"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Recharge Section */}
                  <div className="recharge-section">
                    <h5>Recharge (optional)</h5>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Min</label>
                        <input
                          type="number"
                          value={action.rechargeMin ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            handleActionChange(index, 'rechargeMin', v === '' ? null : (parseInt(v) || 0));
                          }}
                          min="1"
                          max="6"
                        />
                      </div>
                      <div className="form-group">
                        <label>Max</label>
                        <input
                          type="number"
                          value={action.rechargeMax ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            handleActionChange(index, 'rechargeMax', v === '' ? null : (parseInt(v) || 0));
                          }}
                          min="1"
                          max="6"
                        />
                      </div>
                      <div className="form-group">
                        <label>Text Override</label>
                        <input
                          type="text"
                          value={action.rechargeText || ''}
                          onChange={(e) => handleActionChange(index, 'rechargeText', e.target.value)}
                          placeholder="e.g., Recharge 5-6"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Damage Entries Section */}
                  <div className="damage-entries-section">
                    <h5>Damage Entries</h5>
                    {Array.isArray(action.damageEntries) && action.damageEntries.length > 0 ? (
                      action.damageEntries.map((entry, entryIndex) => (
                        <div key={entryIndex} className="damage-entry">
                          <div className="form-row">
                            <div className="form-group">
                              <label>Damage Type</label>
                              <select
                                value={entry.type || 'bludgeoning'}
                                onChange={(e) => handleDamageEntryChange(index, entryIndex, 'type', e.target.value)}
                              >
                                {/* Physical */}
                                <option value="bludgeoning">Bludgeoning</option>
                                <option value="piercing">Piercing</option>
                                <option value="slashing">Slashing</option>
                                {/* Energy */}
                                <option value="fire">Fire</option>
                                <option value="cold">Cold</option>
                                <option value="lightning">Lightning</option>
                                <option value="thunder">Thunder</option>
                                <option value="poison">Poison</option>
                                <option value="acid">Acid</option>
                                <option value="force">Force</option>
                                {/* Special */}
                                <option value="necrotic">Necrotic</option>
                                <option value="radiant">Radiant</option>
                                <option value="psychic">Psychic</option>
                              </select>
                            </div>
                            <div className="form-group">
                              <label>Damage Formula</label>
                              <input
                                type="text"
                                value={entry.formula || ''}
                                onChange={(e) => handleDamageEntryChange(index, entryIndex, 'formula', e.target.value)}
                                placeholder="e.g., 1d6+2"
                              />
                            </div>
                            {action.damageEntries.length > 1 && (
                              <div className="form-group">
                                <label>&nbsp;</label>
                                <button 
                                  onClick={() => removeDamageEntry(index, entryIndex)}
                                  className="btn btn-danger btn-sm"
                                  type="button"
                                >
                                  Remove
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="damage-entry">
                        <div className="form-row">
                          <div className="form-group">
                            <label>Damage Type</label>
                            <select
                              value={action.damageType || 'bludgeoning'}
                              onChange={(e) => handleActionChange(index, 'damageType', e.target.value)}
                            >
                              <option value="bludgeoning">Bludgeoning</option>
                              <option value="piercing">Piercing</option>
                              <option value="slashing">Slashing</option>
                              <option value="fire">Fire</option>
                              <option value="cold">Cold</option>
                              <option value="lightning">Lightning</option>
                              <option value="poison">Poison</option>
                              <option value="acid">Acid</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label>Damage</label>
                            <input
                              type="text"
                              value={action.damage || ''}
                              onChange={(e) => handleActionChange(index, 'damage', e.target.value)}
                              placeholder="e.g., 1d6+2"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    <button 
                      onClick={() => addDamageEntry(index)} 
                      className="btn btn-outline btn-sm"
                      style={{ marginTop: '8px' }}
                      type="button"
                    >
                      + Add Damage Type
                    </button>
                  </div>

                  {/* Saving Throw Section */}
                  {action.attackType === 'save' && (
                    <div className="saving-throw-section">
                      <h5>Saving Throw</h5>
                      
                      <div className="form-row">
                        <div className="form-group">
                          <label>Ability</label>
                          <select
                            value={(action.savingThrow?.ability) || 'dex'}
                            onChange={(e) => handleActionChange(index, 'savingThrow', {
                              ...action.savingThrow,
                              ability: e.target.value
                            })}
                          >
                            <option value="str">Strength</option>
                            <option value="dex">Dexterity</option>
                            <option value="con">Constitution</option>
                            <option value="int">Intelligence</option>
                            <option value="wis">Wisdom</option>
                            <option value="cha">Charisma</option>
                          </select>
                        </div>
                        
                        <div className="form-group">
                          <label>DC</label>
                          <input
                            type="number"
                            value={(action.savingThrow?.dc) || 10}
                            onChange={(e) => handleActionChange(index, 'savingThrow', {
                              ...action.savingThrow,
                              dc: parseInt(e.target.value) || 10
                            })}
                          />
                        </div>
                      </div>
                      
                      <div className="form-row">
                        <div className="form-group">
                          <label>On Success</label>
                          <select
                            value={(action.savingThrow?.success) || 'half'}
                            onChange={(e) => handleActionChange(index, 'savingThrow', {
                              ...action.savingThrow,
                              success: e.target.value
                            })}
                          >
                            <option value="half">Half damage</option>
                            <option value="none">No damage</option>
                            <option value="other">Other effect</option>
                          </select>
                        </div>
                      </div>
                      
                      <div className="form-group">
                        <label>Notes</label>
                        <input
                          type="text"
                          value={(action.savingThrow?.notes) || ''}
                          onChange={(e) => handleActionChange(index, 'savingThrow', {
                            ...action.savingThrow,
                            notes: e.target.value
                          })}
                          placeholder="e.g., on success, immune for 24 hours"
                        />
                      </div>

                      <div className="form-group">
                        <label>Failure Effect</label>
                        <input
                          type="text"
                          value={(action.savingThrow?.failEffect) || ''}
                          onChange={(e) => handleActionChange(index, 'savingThrow', {
                            ...action.savingThrow,
                            failEffect: e.target.value
                          })}
                          placeholder="e.g., frightened for 1 minute; can repeat save each turn"
                        />
                      </div>
                    </div>
                  )}

                  {/* Additional Effects */}
                  <div className="form-group">
                    <label>Additional Effects</label>
                    <input
                      type="text"
                      value={action.additionalEffects || ''}
                      onChange={(e) => handleActionChange(index, 'additionalEffects', e.target.value)}
                      placeholder="e.g., target is grappled (escape DC 14)"
                    />
                  </div>

                  <div className="form-group">
                    <label>Description (auto-generated, but can be edited)</label>
                    <textarea
                      value={action.description || ''}
                      onChange={(e) => handleActionChange(index, 'description', e.target.value)}
                      rows="3"
                      className="full-width"
                    />
                  </div>
                </div>
              ))}
              
              <button onClick={addAction} className="btn btn-outline">
                + Add Action
              </button>
            </div>

            <div className="form-section">
              <h3>Special Properties</h3>
              
              <div className="form-group">
                <label>Damage Immunities (comma-separated)</label>
                <input
                  type="text"
                  value={monsterData.damageImmunities.join(', ')}
                  onChange={(e) => handleArrayFieldChange('damageImmunities', e.target.value)}
                  placeholder="e.g., poison, fire"
                />
              </div>

              <div className="form-group">
                <label>Damage Resistances (comma-separated)</label>
                <input
                  type="text"
                  value={monsterData.damageResistances.join(', ')}
                  onChange={(e) => handleArrayFieldChange('damageResistances', e.target.value)}
                  placeholder="e.g., cold, necrotic"
                />
              </div>

              <div className="form-group">
                <label>Condition Immunities (comma-separated)</label>
                <input
                  type="text"
                  value={monsterData.conditionImmunities.join(', ')}
                  onChange={(e) => handleArrayFieldChange('conditionImmunities', e.target.value)}
                  placeholder="e.g., charmed, frightened"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonsterEditor;
